import {
  Card,
  CardHeader,
  CardFooter,
  Table,
  Container,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import Header from "components/Headers/Header.js";
import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import Pagination from "components/Pagination";
import { getSurveyResultList } from "services/_survey";
import { getMe } from "context/AuthContext";
import { exportSurveyResults } from "services/_survey";
import * as XLSX from "xlsx";

const SurveyResult = () => {
  const me = getMe();
  const isInternalAdmin = me?.partner === "DEFAULT";

  const [showFilters, setShowFilters] = useState(false);

  const today = moment().format("YYYY-MM-DD");
  const firstDayOfYear = moment().startOf("year").format("YYYY-MM-DD");

  const [partner, setPartner] = useState(
    isInternalAdmin ? "DEFAULT" : me?.partner,
  );

  const [fromDate, setFromDate] = useState(firstDayOfYear);
  const [toDate, setToDate] = useState(today);
  const [ageFrom, setAgeFrom] = useState("");
  const [ageTo, setAgeTo] = useState("");
  const [sex, setSex] = useState("");
  const [ethnicity, setEthnicity] = useState("");
  const [race, setRace] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    partner: isInternalAdmin ? "DEFAULT" : me?.partner,
    from_date: firstDayOfYear,
    to_date: today,
  });

  const [surveyData, setSurveyData] = useState([]);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const isSAA = partner === "SAA";
  const showUserInfoFields =
    appliedFilters.partner && appliedFilters.partner !== "DEFAULT";

  const fetchData = useCallback(async () => {
    let payload = `page=${page}&per_page=${perPage}`;

    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) payload += `&${key}=${value}`;
    });

    const data = await getSurveyResultList(payload);

    if (data?.data) {
      setSurveyData(data.data.records || []);
      setRecords(data.data.records || []);
      setTotalRecords(data.data.total_records || 0);
      setTotalPages(Math.ceil((data.data.total_records || 0) / perPage));
    }
  }, [appliedFilters, page, perPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyFilter = () => {
    setPage(1);
    setAppliedFilters({
      from_date: fromDate,
      to_date: toDate,
      partner,
      ...(isSAA && {
        age_from: ageFrom,
        age_to: ageTo,
        sex,
        ethnicity,
        race,
      }),
    });
  };

  const handleResetFilter = () => {
    setPartner(isInternalAdmin ? "DEFAULT" : me?.partner);
    setFromDate(firstDayOfYear);
    setToDate(today);
    setAgeFrom("");
    setAgeTo("");
    setSex("");
    setEthnicity("");
    setRace("");
    setPage(1);

    setAppliedFilters({
      from_date: firstDayOfYear,
      to_date: today,
      partner: isInternalAdmin ? "DEFAULT" : me?.partner,
    });
  };

  const prevButton = () => page > 1 && setPage(page - 1);
  const nextButton = () => totalPages > page && setPage(page + 1);

  const buildExportQuery = () => {
    let query = "";

    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) {
        query += `${query ? "&" : ""}${key}=${encodeURIComponent(value)}`;
      }
    });

    return query;
  };
  const handleExport = async (format) => {
    try {
      const query = buildExportQuery();
      const response = await exportSurveyResults(query, format);

      if (!response.isSuccess || !response.data?.length) {
        return alert("No data to export");
      }

      // Convert JSON → worksheet
      const worksheet = XLSX.utils.json_to_sheet(response.data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Survey Results");

      // Download file
      XLSX.writeFile(
        workbook,
        format === "csv" ? "survey-results.csv" : "survey-results.xlsx",
      );
    } catch (err) {
      console.error(err);
      alert("Export failed");
    }
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center">
                  <h3 className="mb-3 mb-md-0">Survey Result</h3>

                  <div className="ml-md-auto mt-1 d-flex flex-wrap gap-2">
                    <Button
                      color="primary"
                      className="px-3"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      Filters
                    </Button>

                    <Button
                      color="success"
                      className="px-3 mt-1"
                      onClick={() => handleExport("csv")}
                    >
                      Export CSV
                    </Button>
                    <Button
                      color="info"
                      className="px-3 mt-1"
                      onClick={() => handleExport("excel")}
                    >
                      Export Excel
                    </Button>
                  </div>
                </div>
              </CardHeader>

              {/* ---------------- FILTER SECTION ---------------- */}
              {showFilters && (
                <div className="px-4 pb-4 border-bottom">
                  <Row>
                    {isInternalAdmin && (
                      <Col xs="12" md="3">
                        <FormGroup>
                          <Label>Partner</Label>
                          <Input
                            type="select"
                            value={partner}
                            onChange={(e) => setPartner(e.target.value)}
                          >
                            <option value="DEFAULT">DEFAULT</option>
                            <option value="SAA">SAA</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    )}

                    <Col xs="12" md="3">
                      <FormGroup>
                        <Label>From Date</Label>
                        <Input
                          type="date"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                        />
                      </FormGroup>
                    </Col>

                    <Col xs="12" md="3">
                      <FormGroup>
                        <Label>To Date</Label>
                        <Input
                          type="date"
                          value={toDate}
                          min={fromDate}
                          max={today}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      </FormGroup>
                    </Col>

                    {isSAA && (
                      <>
                        <Col xs="12" md="3">
                          <FormGroup>
                            <Label>Age From</Label>
                            <Input
                              type="number"
                              value={ageFrom}
                              onChange={(e) => setAgeFrom(e.target.value)}
                            />
                          </FormGroup>
                        </Col>

                        <Col xs="12" md="3">
                          <FormGroup>
                            <Label>Age To</Label>
                            <Input
                              type="number"
                              value={ageTo}
                              onChange={(e) => setAgeTo(e.target.value)}
                            />
                          </FormGroup>
                        </Col>

                        <Col xs="12" md="3">
                          <FormGroup>
                            <Label>Sex</Label>
                            <Input
                              type="select"
                              value={sex}
                              onChange={(e) => setSex(e.target.value)}
                            >
                              <option value="">All</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              {/* <option value="other">Other</option> */}
                            </Input>
                          </FormGroup>
                        </Col>

                        <Col xs="12" md="3">
                          <FormGroup>
                            <Label>Ethnicity</Label>
                            <Input
                              type="select"
                              value={ethnicity}
                              onChange={(e) => setEthnicity(e.target.value)}
                            >
                              <option value="">All</option>
                              <option value="hispanic or latino">
                                Hispanic or Latino
                              </option>
                              <option value="not hispanic or latino">
                                Not Hispanic or Latino
                              </option>
                              <option value="wish not to disclose">
                                Wish not to disclose
                              </option>
                              {/* <option value="other">Other</option> */}
                            </Input>
                          </FormGroup>
                        </Col>

                        <Col xs="12" md="3">
                          <FormGroup>
                            <Label>Race</Label>
                            <Input
                              type="select"
                              value={race}
                              onChange={(e) => setRace(e.target.value)}
                            >
                              <option value="">All</option>
                              <option value="white">White </option>
                              <option value="black or african american">
                                Black or African American
                              </option>
                              <option value="asian">Asian</option>
                              <option value="american indian or alaska native">
                                American Indian or Alaska Native
                              </option>
                              <option value="native hawaiian or other pacific islander">
                                Native Hawaiian or Other Pacific Islander{" "}
                              </option>
                              <option value="mixed">Mixed </option>
                              <option value="wish not to disclose">
                                Wish to not disclose{" "}
                              </option>
                              {/* <option value="other">Other</option> */}
                            </Input>
                          </FormGroup>
                        </Col>
                      </>
                    )}
                  </Row>

                  <Row className="mt-3">
                    <Col>
                      <Button
                        color="info"
                        size="md"
                        onClick={handleResetFilter}
                      >
                        Reset
                      </Button>
                      <Button
                        color="primary"
                        size="md"
                        className="ml-2"
                        onClick={handleApplyFilter}
                      >
                        Apply
                      </Button>
                    </Col>
                  </Row>
                </div>
              )}

              {/* ---------------- TABLE ---------------- */}
              <Table responsive className="table-flush">
                <thead className="thead-light">
                  <tr>
                    <th>#</th>
                    {showUserInfoFields && (
                      <>
                        <th>Age</th>
                        <th>Sex</th>
                        <th>ethnicity</th>
                        <th>race</th>
                      </>
                    )}
                    <th>Prior diagnosis</th>
                    <th>Symptoms Result</th>
                    <th>Total Score</th>
                    <th>Diagnostics Result</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {surveyData.length ? (
                    surveyData.map((survey, index) => {
                      const getAnswer = (key) =>
                        survey.questions?.find(
                          (q) => q.question === key && q.type === "other",
                        )?.answer || "-";

                      return (
                        <tr key={index}>
                          <td>{(page - 1) * perPage + index + 1}</td>
                          {showUserInfoFields && (
                            <>
                              <td>{getAnswer("age")}</td>
                              <td>{getAnswer("sex")}</td>
                              <td>{getAnswer("ethnicity")}</td>
                              <td>{getAnswer("race")}</td>
                            </>
                          )}
                          <td>
                            {survey.questions?.find(
                              (q) => q.type === "prior_diagnosis",
                            )?.answer || "-"}
                          </td>
                          <td>{survey.symptoms_result}%</td>
                          <td>{survey.symptoms_score}</td>
                          <td>{survey.diagnostics_result}%</td>
                          <td>
                            {moment(survey.created_at).format("DD MMM YYYY")}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center">
                        No record found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              <CardFooter>
                <Pagination
                  page={page}
                  perPage={perPage}
                  totalRecords={totalRecords}
                  totalPages={totalPages}
                  records={records}
                  prevButton={prevButton}
                  nextButton={nextButton}
                />
              </CardFooter>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};
export default SurveyResult;
